import React, { useCallback, useEffect, useState, createContext, useContext } from 'react';
import { fetchProductPrices, type StripePriceMap } from '../utils/stripe';
import { shopData } from '../components/pages/site/shopData';
import { bookData } from '../components/pages/site/bookData';
interface PriceContextValue {
  prices: StripePriceMap;
  loading: boolean;
  error: string | null;
  /** Look up a single price; returns null if not yet loaded or unknown */
  getPriceCents: (priceId: string | undefined) => number | null;
  /** Manually refresh prices from Stripe */
  refresh: () => Promise<void>;
}
const PriceContext = createContext<PriceContextValue | undefined>(undefined);
/** Collect every known Stripe Price ID across the catalog */
function getAllPriceIds(): string[] {
  const ids = new Set<string>();
  for (const p of shopData) {
    if (p.stripePriceId) ids.add(p.stripePriceId);
  }
  for (const b of bookData) {
    if (b.stripePriceId) ids.add(b.stripePriceId);
  }
  return Array.from(ids);
}
export function PriceProvider({
  children
}: {
  children: ReactNode;
}) {
  const [prices, setPrices] = useState<StripePriceMap>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const refresh = useCallback(async () => {
    const priceIds = getAllPriceIds();
    if (priceIds.length === 0) {
      setPrices({});
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await fetchProductPrices(priceIds);
      setPrices(result);
    } catch (err: any) {
      // Silent failure is fine here — components fall back to display prices
      // from shopData / bookData. Log for debugging.
      console.warn('[Prices] Could not fetch live prices from Stripe:', err?.message ?? err);
      setError(err?.message ?? 'Could not load prices');
    } finally {
      setLoading(false);
    }
  }, []);
  // Fetch on mount
  useEffect(() => {
    refresh();
  }, [refresh]);
  // Refetch when tab regains focus — keeps prices fresh after price changes
  useEffect(() => {
    const onFocus = () => {
      refresh();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);
  const getPriceCents = useCallback((priceId: string | undefined): number | null => {
    if (!priceId) return null;
    return prices[priceId]?.amount ?? null;
  }, [prices]);
  const value: PriceContextValue = {
    prices,
    loading,
    error,
    getPriceCents,
    refresh
  };
  return <PriceContext.Provider value={value} data-id="element-2648">{children}</PriceContext.Provider>;
}
export function usePrices() {
  const ctx = useContext(PriceContext);
  if (!ctx) throw new Error('usePrices must be used inside <PriceProvider>');
  return ctx;
}
/**
 * Convenience hook to get a single product's current price (in cents).
 * Returns:
 *   - the live Stripe price if loaded
 *   - the provided fallback price (display hint from shopData/bookData) if Stripe hasn't loaded
 *   - null if neither is available
 */
export function usePrice(priceId: string | undefined, fallbackCents?: number): {
  cents: number | null;
  isLive: boolean;
  loading: boolean;
} {
  const {
    prices,
    loading
  } = usePrices();
  const live = priceId ? prices[priceId]?.amount : undefined;
  if (live !== undefined) {
    return {
      cents: live,
      isLive: true,
      loading
    };
  }
  if (fallbackCents !== undefined) {
    return {
      cents: fallbackCents,
      isLive: false,
      loading
    };
  }
  return {
    cents: null,
    isLive: false,
    loading
  };
}